import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RequestTemplateService {

  private readonly BASE_ENDPOINT = 'packages';

  constructor(private api: ApiService) { }

  /**
   * List request templates with pagination and sorting
   * @param rowsPerView Number of items per page
   * @param pageNumber Current page number (1-based)
   * @param search Search term to filter by name
   * @param sortString Sort string (e.g., 'name', '-name', 'created', '-created')
   * @param status Status filter(s) - can be 'active', 'inactive', or 'disabled'. Can be a string or array of strings.
   * @returns Observable with templates and total count
   */
  listTemplates(rowsPerView = 20, pageNumber = 1, search?: string, sortString?: string, status?: string | string[]): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/list`;
    let params = new HttpParams();

    if (rowsPerView !== 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }

    if (sortString) {
      params = params.set('sort', sortString);
    }

    if (search) {
      params = params.set('search', search);
    }

    if (status) {
      const statusArray = Array.isArray(status) ? status : [status];
      statusArray.forEach(statusValue => {
        params = params.append('status', statusValue);
      });
    }

    return this.api.get(endpoint, { params }).pipe(
      map(response => ({
        templates: response.data?.templates || [],
        total: response.data?.total || 0
      }))
    );
  }

  /**
   * Get a single template by ID
   * @param templateId Template ID
   * @returns Observable with template data
   */
  getTemplate(templateId: number): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/${templateId}`;
    return this.api.get(endpoint).pipe(
      map(response => response.data?.template || null)
    );
  }

  /**
   * Activate a template (toggle-activate when currently inactive)
   * @param templateId Template ID
   * @returns Observable with updated template
   */
  activateTemplate(templateId: number): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/${templateId}/toggle-activate`;
    return this.api.post(endpoint, {}).pipe(
      map(response => response.data?.template || null)
    );
  }

  /**
   * Deactivate a template (toggle-activate when currently active)
   * @param templateId Template ID
   * @returns Observable with updated template
   */
  deactivateTemplate(templateId: number): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/${templateId}/toggle-activate`;
    return this.api.post(endpoint, {}).pipe(
      map(response => response.data?.template || null)
    );
  }

  /**
   * Rename a template
   * @param templateId Template ID
   * @param templateName New name for the template
   * @returns Observable with updated template
   */
  renameTemplate(templateId: number, templateName: string): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/${templateId}/rename`;
    return this.api.post(endpoint, { name: templateName }).pipe(
      map(response => response.data?.template || null)
    );
  }

  /**
   * Delete a template
   * @param templateId Template ID
   * @returns Observable with success response
   */
  deleteTemplate(templateId: number): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/${templateId}/delete`;
    return this.api.delete(endpoint).pipe(
      map(response => ({
        success: true,
        template: response.data?.template || null
      }))
    );
  }

  /**
   * Create a new template
   * @param templateData Template data
   * @returns Observable with created template
   */
  createTemplate(templateData: any): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/create`;
    return this.api.post(endpoint, templateData).pipe(
      map(response => response.data?.template || null)
    );
  }

  /**
   * Update a template
   * @param templateId Template ID
   * @param templateData Updated template data
   * @returns Observable with updated template
   */
  updateTemplate(templateId: number, templateData: any): Observable<any> {
    const endpoint = `${this.BASE_ENDPOINT}/${templateId}/update`;
    return this.api.put(endpoint, templateData).pipe(
      map(response => response.data?.template || null)
    );
  }

  getNewTitle(baseTitle){
      let params = new HttpParams().set('name', baseTitle);
      const path = `${this.BASE_ENDPOINT}/check-name`;
      return this.api.get(path, {params}).pipe(map(data=>data['data']));
  }
}
